/**
 * ReadinessProfilesDialog — configure weight profiles & thresholds
 * (attendance-status-config pattern: org default + named profiles that can
 * be pinned to departments or to the current batch). Also hosts the
 * org-level agent self-view toggle (default OFF).
 */
import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { useApi } from '../../hooks/useApi';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useTraineeFilter } from './TraineeFilterContext';

const AUTO = '__auto__';

export default function ReadinessProfilesDialog({ open, onOpenChange, batchId, onChanged }) {
  const { get, post, put, del } = useApi();
  const { departments } = useTraineeFilter();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);          // selected profile_id
  const [draft, setDraft] = useState(null);      // editable copy
  const [batchProfile, setBatchProfile] = useState(AUTO);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    get('/readiness/profiles').then((d) => {
      setData(d);
      const first = d.profiles.find((p) => p.is_default) || d.profiles[0];
      setSel((s) => s || first?.profile_id);
    }).catch(() => {});
  }, [get]);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    const p = data?.profiles.find((x) => x.profile_id === sel);
    setDraft(p ? {
      weights: { ...p.weights }, thresholds: { ...p.thresholds },
      min_coverage: p.min_coverage, time_escalation: p.time_escalation,
      department_ids: p.department_ids || [],
    } : null);
  }, [data, sel]);

  const profile = data?.profiles.find((x) => x.profile_id === sel);

  const save = async () => {
    setSaving(true);
    try {
      await put(`/readiness/profiles/${sel}`, draft);
      toast.success('Profile saved — a new version takes effect from now');
      load(); onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const createProfile = async () => {
    const name = window.prompt('Profile name (e.g. "Voice nesting")');
    if (!name?.trim()) return;
    try {
      const p = await post('/readiness/profiles', { name: name.trim() });
      toast.success('Profile created');
      await load(); setSel(p.profile_id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };

  const removeProfile = async () => {
    try {
      await del(`/readiness/profiles/${sel}`);
      toast.success('Profile deleted');
      setSel(null); load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const assignBatch = async (value) => {
    setBatchProfile(value);
    try {
      await put(`/readiness/batch/${batchId}/profile`,
        { profile_id: value === AUTO ? null : value });
      toast.success(value === AUTO ? 'Batch uses auto-resolved profile' : 'Profile pinned to batch');
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleAgentView = async (v) => {
    try {
      await put('/readiness/settings', { agent_view_enabled: v });
      setData((d) => ({ ...d, settings: { ...d.settings, agent_view_enabled: v } }));
      toast.success(v ? 'Agents can now see their own readiness' : 'Agent self-view disabled');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const setGating = async (v) => {
    try {
      await put('/readiness/settings', { readiness_gating: v });
      setData((d) => ({ ...d, settings: { ...d.settings, readiness_gating: v } }));
      toast.success(
        v === 'required' ? 'Certification now requires floor-ready readiness'
          : v === 'advisory' ? 'Readiness shown on eligibility (advisory only)'
            : 'Readiness gating turned off');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="readiness-profiles-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Readiness profiles</DialogTitle>
          <DialogDescription>
            Weights and thresholds per profile. Resolution: batch pin → department profile → org default.
            Edits create a new version — historical scores are never rewritten.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap" data-testid="profile-selector">
          {data.profiles.map((p) => (
            <Button key={p.profile_id} size="sm"
                    variant={p.profile_id === sel ? 'default' : 'outline'}
                    onClick={() => setSel(p.profile_id)}
                    data-testid={`profile-pill-${p.profile_id}`}>
              {p.name} <span className="ml-1 opacity-60">v{p.version}</span>
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={createProfile} data-testid="profile-create-btn">
            <Plus className="w-4 h-4 mr-1" /> New
          </Button>
        </div>

        {profile && draft && (
          <div className="space-y-5 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Input weights (0 = off)</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {data.catalog.map((c) => (
                  <div key={c.key} className="flex items-center justify-between gap-2">
                    <span className="text-sm">{c.label}</span>
                    <Input type="number" min={0} max={100} className="w-20 h-8"
                           value={draft.weights[c.key] ?? 0}
                           onChange={(e) => setDraft((d) => ({
                             ...d, weights: { ...d.weights, [c.key]: Number(e.target.value) } }))}
                           data-testid={`weight-input-${c.key}`} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">State thresholds</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {['floor_ready', 'on_track', 'not_ready'].map((k) => (
                  <div key={k}>
                    <p className="text-xs text-muted-foreground mb-1">{k.replace('_', ' ')}</p>
                    <Input type="number" min={0} max={100} className="h-8"
                           value={draft.thresholds[k] ?? ''}
                           onChange={(e) => setDraft((d) => ({
                             ...d, thresholds: { ...d.thresholds, [k]: Number(e.target.value) } }))}
                           data-testid={`threshold-input-${k}`} />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Time escalation</p>
                <p className="text-xs text-muted-foreground">On-track drops to at-risk when &lt;20% of batch time remains</p>
              </div>
              <Switch checked={draft.time_escalation}
                      onCheckedChange={(v) => setDraft((d) => ({ ...d, time_escalation: v }))}
                      data-testid="time-escalation-switch" />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Minimum inputs for a score</p>
                <p className="text-xs text-muted-foreground">Below this coverage a trainee shows “Not scored”</p>
              </div>
              <Input type="number" min={1} max={7} className="w-20 h-8"
                     value={draft.min_coverage}
                     onChange={(e) => setDraft((d) => ({ ...d, min_coverage: Number(e.target.value) }))}
                     data-testid="min-coverage-input" />
            </div>

            {!profile.is_default && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Applies to departments</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="profile-departments">
                  {departments.map((dep) => {
                    const on = draft.department_ids.includes(dep.department_id);
                    return (
                      <Badge key={dep.department_id} variant="outline"
                             className={`cursor-pointer ${on ? 'bg-primary text-primary-foreground' : ''}`}
                             onClick={() => setDraft((d) => ({
                               ...d,
                               department_ids: on
                                 ? d.department_ids.filter((x) => x !== dep.department_id)
                                 : [...d.department_ids, dep.department_id],
                             }))}
                             data-testid={`profile-dept-${dep.department_id}`}>
                        {dep.name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-1 border-t">
              <div className="flex items-center gap-2">
                <Button onClick={save} disabled={saving} data-testid="profile-save-btn">
                  {saving ? 'Saving…' : 'Save profile'}
                </Button>
                {!profile.is_default && (
                  <Button variant="ghost" className="text-rose-600" onClick={removeProfile}
                          data-testid="profile-delete-btn">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {batchId && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">This batch uses:</span>
                  <Select value={batchProfile} onValueChange={assignBatch}>
                    <SelectTrigger className="w-44 h-8" data-testid="batch-profile-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={AUTO}>Auto (dept / default)</SelectItem>
                      {data.profiles.map((p) => (
                        <SelectItem key={p.profile_id} value={p.profile_id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
              <div>
                <p className="text-sm font-medium">Agent self-view</p>
                <p className="text-xs text-muted-foreground">
                  Show each agent a simplified readiness card in their portal (score + focus areas, no weights or peer data)
                </p>
              </div>
              <Switch checked={data.settings.agent_view_enabled}
                      onCheckedChange={toggleAgentView}
                      data-testid="agent-view-switch" />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
              <div>
                <p className="text-sm font-medium">Certification gating</p>
                <p className="text-xs text-muted-foreground">
                  Off: eligibility unchanged · Advisory: readiness shown for context ·
                  Required: only floor-ready trainees can be certified
                </p>
              </div>
              <Select value={data.settings.readiness_gating || 'off'} onValueChange={setGating}>
                <SelectTrigger className="w-32 h-8" data-testid="readiness-gating-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="advisory">Advisory</SelectItem>
                  <SelectItem value="required">Required</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
